import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Cpu, CheckCircle2, TrendingDown } from 'lucide-react';

/**
 * IdealInstanceTypesPanel — "are these the right node shapes for this
 * cluster?" (CAST/CloudPilot instance-type-recommendation parity).
 *
 * Reads GET /v1/clusters/{id}/node-optimization/pool-shape-recommendations —
 * the pure pool-shape recommender run over the latest node snapshot:
 * workloads bucketed by CPU:mem ratio, compared against the pools' current
 * instance families. Two truthful states:
 *   - recommendations[] non-empty: a workload cluster is mis-shaped for the
 *     current pools -> show suggested family, size, $/mo, reason.
 *   - empty with workloads counted: shapes are covered -> show the current
 *     types with a "well-matched" check (the engine's bin-pack picked them).
 * Quietly renders NOTHING on error/404 (older backend) or when no snapshot.
 */
const IdealInstanceTypesPanel = ({ clusterId }) => {
  const [data, setData] = useState(null);
  const [gone, setGone] = useState(false);

  const load = useCallback(async () => {
    if (!clusterId) return;
    try {
      const r = await axios.get(
        `${API}/v1/clusters/${clusterId}/node-optimization/pool-shape-recommendations`,
      );
      setData(r.data);
    } catch {
      setGone(true); // older backend / no access — hide entirely
    }
  }, [clusterId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 300000); // shapes drift slowly — 5 min
    return () => clearInterval(id);
  }, [load]);

  if (!clusterId || gone || !data || !data.workload_count) return null;

  const recs = data.recommendations || [];
  const current = data.current_instance_types || [];
  const totalSave = recs.reduce((s, r) => s + (r.estimated_monthly_savings || 0), 0);

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Cpu className="h-4 w-4 text-indigo-600" />
          Ideal instance types
          <Badge className="bg-slate-100 text-slate-700 border-slate-200">
            {data.workload_count} workloads · {data.cloud_provider}
          </Badge>
          {totalSave > 0 && (
            <Badge className="bg-emerald-100 text-emerald-800">
              <TrendingDown className="h-3 w-3 mr-1" />
              ${totalSave.toFixed(0)}/mo available
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-gray-500 mb-2">
          Current pool types:{' '}
          {current.map((t) => (
            <code key={t} className="text-[11px] bg-gray-100 rounded px-1 mr-1">{t}</code>
          ))}
        </p>
        {recs.length === 0 ? (
          <p className="text-xs text-gray-600 inline-flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            Workload shapes are well-matched to these pools — no better family
            found. (The engine re-evaluates this on every provisioning cycle.)
          </p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-2 py-1.5">Workload shape</th>
                  <th className="text-left px-2 py-1.5">Suggested family</th>
                  <th className="text-right px-2 py-1.5">Workloads</th>
                  <th className="text-right px-2 py-1.5">CPU / Mem</th>
                  <th className="text-right px-2 py-1.5">$/mo</th>
                </tr>
              </thead>
              <tbody>
                {recs.map((r) => (
                  <tr key={r.bucket} className="border-t hover:bg-gray-50" title={r.reason}>
                    <td className="px-2 py-1.5">{r.bucket.replace('_', ' ')}</td>
                    <td className="px-2 py-1.5">
                      <code className="text-[11px] bg-indigo-50 text-indigo-700 rounded px-1">
                        {r.suggested_family}
                      </code>
                      {r.closest_existing_family && (
                        <span className="text-gray-400 ml-1">now: {r.closest_existing_family}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">{r.workload_count}</td>
                    <td className="px-2 py-1.5 text-right">
                      {r.total_cpu_cores?.toFixed(1)}c / {r.total_memory_gib?.toFixed(0)}Gi
                    </td>
                    <td className="px-2 py-1.5 text-right font-medium">
                      ${Number(r.estimated_monthly_savings || 0).toFixed(0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[10px] text-gray-400 mt-2">
          Workloads bucketed by CPU:memory ratio vs current pool families — the
          same shape math the engine's bin-pack applies when it buys nodes.
          Hover a row for the reasoning.
        </p>
      </CardContent>
    </Card>
  );
};

export default IdealInstanceTypesPanel;
